import React, { useEffect, useState } from 'react';
import { NavLink , useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash-es/get';
import _noop from 'lodash-es/noop';
import H4 from 'sushiweb/Typography/Heading/H4';
import Layout from 'sushiweb/Layout/Layout';
import Button from 'sushiweb/Button/Button';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import { SingleLineArrow } from '../../../components/SVGs/commonSVGs';
import {
  ENTERPRISE_HOST,
  GET_AUTH_CHECK,
  GET_AUTH_VALIDATE,
  GET_PAYMENT_CODE,
  GET_PAYMENT_REQUEST_LOG,
} from '../../../constants/urls';
import zfetch, { METHOD_GET, METHOD_POST } from '../../common/utils/zfetch';
import {
  HeadWrapper,
  LayoutContainers,
  LoggedOutHeaderWrapper,
  LoggedOutStateWrapper,
  LoggedOutZFELogoWrapper,
  OnlinePaymentHeader,
  SpinnerHolder,
} from '../../common/styles';

import {
  clearCookie,
  formatAmountInRupees,
  getPaymentRequestLogStatusMessage,
  getServiceTypeFromFlowType,
  isConnectedToStagging,
} from '../../../helpers';
import { LoggedOutCheckoutBtnContainer } from '../styles';
import ErrorPage from '../../common/components/ErrorPage/ErrorPage';
import PaymentKit from '../OnlinePaymentCheckoutPage/PaymentKit';
import {
  HeaderCenterWrapper,
  HeaderContainer,
} from '../../common/components/Header/styles';
import {
  CROPPED_Z_LOGO_BLK,
  FOOD_AT_WORK_LOGO_IMG,
} from '../../../constants/images';
import {
  currentFlowTypeEnum,
  paymentRequestEnum,
} from '../../common/components/constants';

const LoggedOutOnlinePaymentsPage = ({
  paymentsInfo,
  getOnlinePaymentInfo,
  fetchingOnlinePaymentInfo,
  fetchedOnlinePaymentInfo,
}) => {
  const { paymentRequestID } = useParams();
  const [paymentSDKData, setPaymentSDKData] = useState({});
  const [accessCode, setAccessCode] = useState('');
  const [amountBalance, setAmountBalance] = useState(0);
  const [isAccessCodeFetched, setIsAccessCodeFetched] = useState(true);
  const [isPaymentRequestFetched, setIsPaymentRequestFetched] = useState(false);
  const [paymentRequestStatus, setPaymentRequestStatus] = useState(
    paymentRequestEnum.UNSPECIFIED,
  );
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [paymentKitVisibility, setPaymentKitVisibility] = useState(false);
  const [isAuthCalled, setIsAuthCalled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isValidSPOC, setIsValidSPOC] = useState(false);
  const [spocWorkspace, setSpocWorkspace] = useState('');
  const [redirectPage, setRedirectPage] = useState(false);
  const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);
  const [isCheckoutEnded, setIsCheckoutEnded] = useState(false);
  const [fetchingAccessCode, setFetchingAccessCode] = useState(true);
  const [fetchingPaymentRequest, setFetchingPaymentRequest] = useState(true);
  const [currentFlowType, setCurrentFlowType] = useState(0);
  const checkoutPayment = () => {
    setIsCheckoutStarted(true);
    setLoadingCheckout(true);
  };

  useEffect(() => {
    clearCookie('__Host-csrft').then(
      zfetch(GET_AUTH_VALIDATE, {
        method: METHOD_POST,
      })
        .then(res => res.json())
        .then(data => {
          // can check for logged in state here
          setIsLoggedIn(_get(data, 'response.is_logged_in', false));
          console.log(data);
          setIsAuthCalled(true);
        })
        .catch(err => {
          console.error(err);
          setIsAuthCalled(true);
        }),
    );
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      zfetch(GET_AUTH_CHECK, {
        method: METHOD_GET,
      })
        .then(res => res.json())
        .then(res => {
          const status = _get(res, 'status', false);
          const userID = _get(res, 'data.user_id', 0);
          const validSPOC = status && userID !== 0;
          setIsValidSPOC(validSPOC);
          const workspaces = _get(res, 'data.workspaces', []);
          if (workspaces.length !== 0) {
            setSpocWorkspace(workspaces[0]?.workspace_id ?? '');
          }
        })
        .catch(err => {
          console.error(err);
          setIsValidSPOC(false);
        });
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (isAuthCalled && isValidSPOC) {
      zfetch(GET_PAYMENT_REQUEST_LOG, {
        params: {
          payment_request_id: paymentRequestID,
        },
      })
        .then(res => res.json())
        .then(data => {
          const workspaceID = _get(data, 'data.workspace_id', '');
          if (spocWorkspace === workspaceID) {
            setRedirectPage(true);
          }
        })
        .catch(error => {
          console.error(error);
        });
    }
  }, [isAuthCalled, isValidSPOC]);

  useEffect(() => {
    if (redirectPage) {
      const newPath = `${ENTERPRISE_HOST}/dashboard/#/payments/online-payment/${paymentRequestID}`;
      window.location.replace(newPath);
    }
  }, [redirectPage]);

  useEffect(() => {
    if (isAuthCalled && isPaymentRequestFetched)
      getOnlinePaymentInfo({
        paymentRequestID,
        flowType: getServiceTypeFromFlowType(currentFlowType),
      });
  }, [isAuthCalled, isPaymentRequestFetched]);

  useEffect(() => {
    if (
      !fetchingOnlinePaymentInfo &&
      fetchedOnlinePaymentInfo &&
      paymentsInfo &&
      isPaymentRequestFetched
    ) {
      setPaymentSDKData({
        amount: amountBalance,
        country_id: paymentsInfo.country_id ?? 1,
        service_type: paymentsInfo.service_type ?? '',
        company_id: paymentsInfo.company_id ?? '1',
        online_payments_flag: paymentsInfo.online_payments_flag ?? 1,
        user_details: {
          user_email: paymentsInfo?.user_details?.email ?? '',
          phone: paymentsInfo?.user_details?.phone ?? '',
        },
      });
    }
  }, [fetchingOnlinePaymentInfo, fetchingPaymentRequest]);

  useEffect(() => {
    if (isAuthCalled && isPaymentRequestFetched) {
      zfetch(GET_PAYMENT_CODE, {
        params: {
          service_type: getServiceTypeFromFlowType(currentFlowType),
          payment_request_id: paymentRequestID,
        },
      })
        .then(res => res.json())
        .then(data => {
          setAccessCode(_get(data, 'token'));
          setIsAccessCodeFetched(true);
          setFetchingAccessCode(false);
        })
        .catch(error => {
          setIsAccessCodeFetched(false);
          setFetchingAccessCode(false);
          console.error(error);
        });
    }
  }, [isAuthCalled && isPaymentRequestFetched]);

  useEffect(() => {
    if (isAuthCalled && !isValidSPOC) {
      zfetch(GET_PAYMENT_REQUEST_LOG, {
        params: {
          payment_request_id: paymentRequestID,
        },
      })
        .then(res => res.json())
        .then(data => {
          setAmountBalance(_get(data, 'data.amount', 0));
          setPaymentRequestStatus(_get(data, 'data.status', 0));
          setCurrentFlowType(_get(data, 'data.flow_type', 0));
          setIsPaymentRequestFetched(
            _get(data, 'status_response.status', false),
          );
          setFetchingPaymentRequest(false);
        })
        .catch(error => {
          setIsPaymentRequestFetched(false);
          setFetchingPaymentRequest(false);
          console.error(error);
        });
    }
  }, [isAuthCalled, isValidSPOC]);

  const ZFELogo = () => {
    return (
      <NavLink to={`/online-payment/${paymentRequestID}`}>
        {' '}
        <img
          src={
            currentFlowType === currentFlowTypeEnum.FLOW_TYPE_GIFT_CARD
              ? CROPPED_Z_LOGO_BLK
              : FOOD_AT_WORK_LOGO_IMG
          }
          height='35rem'
          alt='Zomato for enterprise'
        />
      </NavLink>
    );
  };

  if (redirectPage) {
    return (
      <LoggedOutStateWrapper>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </LoggedOutStateWrapper>
    );
  }

  if (
    fetchingOnlinePaymentInfo ||
    fetchingAccessCode ||
    fetchingPaymentRequest
  ) {
    return (
      <LoggedOutStateWrapper>
        <SpinnerHolder className='center'>
          <Spinner color={red.z_red} size='medium' />
        </SpinnerHolder>
      </LoggedOutStateWrapper>
    );
  }
  if (
    paymentRequestStatus !== paymentRequestEnum.ACTIVE &&
    paymentRequestStatus !== paymentRequestEnum.UNSPECIFIED
  ) {
    return (
      <>
        <LoggedOutHeaderWrapper isStagging={isConnectedToStagging()}>
          <HeaderCenterWrapper>
            <HeaderContainer>
              <LoggedOutZFELogoWrapper>
                <ZFELogo />
                {' '}
              </LoggedOutZFELogoWrapper>
            </HeaderContainer>
          </HeaderCenterWrapper>
        </LoggedOutHeaderWrapper>
        <LoggedOutStateWrapper>
          <HeadWrapper>
            <OnlinePaymentHeader>
              <H4>
                {getPaymentRequestLogStatusMessage(
                  paymentRequestStatus,
                  currentFlowType,
                )}
              </H4>
            </OnlinePaymentHeader>
          </HeadWrapper>
        </LoggedOutStateWrapper>
      </>
    );
  }

  return (
    <>
      <LoggedOutHeaderWrapper isStagging={isConnectedToStagging()}>
        <HeaderCenterWrapper>
          <HeaderContainer>
            <LoggedOutZFELogoWrapper>
              <ZFELogo />
              {' '}
            </LoggedOutZFELogoWrapper>
          </HeaderContainer>
        </HeaderCenterWrapper>
      </LoggedOutHeaderWrapper>
      <LoggedOutStateWrapper>
        {fetchedOnlinePaymentInfo &&
          isAccessCodeFetched &&
          isPaymentRequestFetched && (
            <HeadWrapper>
              <OnlinePaymentHeader>
                <H4>
                  Amount to be paid: &nbsp;
                  <div id='amount'>
                    {formatAmountInRupees(Number.parseFloat(amountBalance))}
                  </div>
                </H4>
              </OnlinePaymentHeader>
            </HeadWrapper>
          )}
        <LayoutContainers style={{ padding: '30px 0px 0px 0px' }}>
          <Layout gap={20}>
            <Layout.Item colD={12} colT={12} colM={12}>
              {fetchedOnlinePaymentInfo &&
                isAccessCodeFetched &&
                isPaymentRequestFetched ? (
                  <PaymentKit
                    paymentKitVisibility={paymentKitVisibility}
                    setPaymentKitVisibility={setPaymentKitVisibility}
                    paymentSdkData={paymentSDKData}
                    accessCode={accessCode}
                    isCheckoutStarted={isCheckoutStarted}
                    isCheckoutEnded={isCheckoutEnded}
                    setIsCheckoutEnded={setIsCheckoutEnded}
                    initiateCheckout={checkoutPayment}
                    setIsCheckoutStarted={setIsCheckoutStarted}
                    setLoadingCheckout={setLoadingCheckout}
                    loggedOutInfo={{
                    balance: amountBalance,
                    payment_request_id: paymentRequestID,
                  }}
                    adhocState
                  />
              ) : (
                <ErrorPage />
              )}
            </Layout.Item>
            <LoggedOutCheckoutBtnContainer>
              <Button
                onClick={checkoutPayment}
                loading={loadingCheckout}
                style={{ paddingRight: '30px' }}
                icon={<SingleLineArrow />}
                iconAlign='right'
                disabled={
                  !fetchedOnlinePaymentInfo ||
                  !isAccessCodeFetched ||
                  !isPaymentRequestFetched
                }
              >
                {' '}
                Pay Now
                {' '}
              </Button>
            </LoggedOutCheckoutBtnContainer>
          </Layout>
        </LayoutContainers>
      </LoggedOutStateWrapper>
    </>
  );
};

LoggedOutOnlinePaymentsPage.propTypes = {
  getOnlinePaymentInfo: PropTypes.func,
  paymentsInfo: PropTypes.objectOf(PropTypes.any),
  fetchingOnlinePaymentInfo: PropTypes.bool,
  fetchedOnlinePaymentInfo: PropTypes.bool,
};

LoggedOutOnlinePaymentsPage.defaultProps = {
  getOnlinePaymentInfo: _noop,
  paymentsInfo: {},
  fetchingOnlinePaymentInfo: true,
  fetchedOnlinePaymentInfo: true,
};

export default LoggedOutOnlinePaymentsPage;
